import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { clean, money, num, shortName, stagger, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';
import { petFeeTiers, type FeeLine } from '../utils/fees';
import { feeHead, feeRow, rowAmt, rowApplied, rowFreq, rowMain, rowMeta, rowName } from '../utils/feeRows';

const guideCtaDef: SlotGroupDefinition = {
  label: 'Fee Guide Button',
  description: 'Hidden when the Easy Fee Guide page is hidden or the property has no fee fields.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See the Easy Fee Guide' },
    url: { label: 'URL', type: 'url', defaultValue: '/easy-fee-guide' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** "One fee when you move in, one line on your rent each month. No pet deposit." Clauses without data drop. */
function leadText(fee: number | null, rent: number | null, deposit: number | null): string {
  const first = fee && rent ? 'One fee when you move in, one line on your rent each month.' : fee ? 'One fee when you move in.' : rent ? 'One line on your rent each month.' : '';
  return [first, deposit ? '' : 'No pet deposit.'].filter(Boolean).join(' ');
}

/** The pet fee rows of the Easy Fee Guide, on the Pet Policy page: one-time fee, pet rent, refundable deposit. */
export function Fees() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const flags = useSiteFlags();
  const guide = useSlotGroup('fees.guide_cta', guideCtaDef);

  const fee = num(clean(p.property_upfront_pet_fee)) || null;
  const rent = num(clean(p.property_monthly_pet_rent)) || null;
  const deposit = num(clean(p.property_pet_deposit)) || null;
  const maxPets = num(clean(p.property_max_pets));

  const rows: FeeLine[] = [];
  if (fee) rows.push({ name: 'Pet Fee', amount: money(fee)!, applied: 'Per pet', freq: 'One-time' });
  if (rent) rows.push({ name: 'Pet Rent', amount: money(rent)!, applied: 'Per pet', freq: 'Monthly' });
  if (deposit) rows.push({ name: 'Pet Deposit', amount: money(deposit)!, applied: 'Per pet', freq: 'Refundable' });

  if ((!p.property_ispetfriendly || !rows.length) && !editing) return null;

  const showGuide = flags.shows('easy-fee-guide');
  const name = shortName(clean(p.property_name)) || 'the community';
  // The description's two-pet fee wins over 2 × the one-pet fee so the note never contradicts the policy above it.
  const twoFee = fee ? (petFeeTiers(p)?.[1] ?? fee * 2) : null;
  const twoPets =
    (maxPets == null || maxPets >= 2) && (twoFee || rent)
      ? `Two pets on the lease means ${[
          twoFee && `${money(twoFee)} at move-in${deposit ? `, plus ${money(deposit * 2)} in refundable deposits,` : ''}`,
          rent && `${money(rent * 2)} a month`,
        ]
          .filter(Boolean)
          .join(' and ')}.`
      : '';
  const guideLine = showGuide ? `Every other fee at ${name}, including move-in and monthly essentials, is in the Easy Fee Guide.` : '';
  const note = [twoPets, guideLine].filter(Boolean).join(' ');

  return (
    <section className="section flex flex-col gap-10 max-[1199px]:gap-8 max-[767px]:gap-6" id="pet-fees">
      <div className="flex max-w-[760px] flex-col gap-4 max-[767px]:gap-3" data-reveal>
        <RichTextContent path="fees.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="fees.title" as="h2" className="h2" />
        <RichTextContent
          path="fees.lead"
          as="p"
          className="m-0 text-lg leading-7 text-[--q-muted] [&_p]:m-0 max-[1199px]:text-[17px] max-[1199px]:leading-[27px] max-[767px]:text-[15px] max-[767px]:leading-[22px]"
          defaultValue={toParagraph(leadText(fee, rent, deposit))}
        />
      </div>
      <div className="max-[767px]:border-t max-[767px]:border-t-[--q-border]" data-reveal style={stagger(1)}>
        <div className={feeHead} aria-hidden="true">
          <span>Fee</span>
          <span className="text-right">Amount</span>
          <span className="pl-10 max-[1199px]:pl-6">Applied</span>
          <span>Frequency</span>
        </div>
        {rows.map((l) => (
          <div className={feeRow} key={l.name}>
            <div className={rowMain}>
              <span className={rowName}>{l.name}</span>
              <span className={rowAmt}>{l.amount}</span>
              <span className={rowApplied}>{l.applied}</span>
              <span className={rowFreq}>{l.freq}</span>
              <span className={rowMeta}>{`${l.applied} · ${l.freq}`}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-12 max-[1199px]:flex-col max-[1199px]:items-start max-[1199px]:gap-6 max-[767px]:items-stretch max-[767px]:gap-4">
        <RichTextContent
          path="fees.note"
          as="p"
          className="m-0 max-w-[680px] text-sm leading-5 text-[--q-muted] [&_p]:m-0 max-[767px]:text-[13px] max-[767px]:leading-[19px]"
          defaultValue={toParagraph(note)}
        />
        <EditableSlotGroup groupPath="fees.guide_cta" definition={guideCtaDef} className="shrink-0 empty:hidden">
          {showGuide && (
            <a className="btn btn-secondary" href={guide.url || PATHS.feeGuide} target={guide._target || '_self'}>
              {guide.label || 'See the Easy Fee Guide'} <span className="btn__arrow">→</span>
            </a>
          )}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Fees',
  displayName: 'Pet Fees',
  tokens: {
    'fees.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Pet fees</p>' },
    'fees.title': { label: 'Title', type: 'richtext', defaultValue: '<p>What your pets cost.</p>' },
    'fees.lead': {
      label: 'Lead',
      description: 'Until edited, names the one-time fee and pet rent the property charges, and says "No pet deposit." while the pet deposit is empty.',
      type: 'richtext',
      defaultValue: '<p>One fee when you move in, one line on your rent each month.</p>',
    },
    'fees.note': {
      label: 'Footnote',
      description: 'Until edited, gives the move-in and monthly cost of two pets and points to the Easy Fee Guide.',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'fees.guide_cta': guideCtaDef,
  },
};

(Fees as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Fees;
